import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/T.png';
import { blogContent } from './PublicPages/Blog/BlogPost';

const featuredPosts = Object.entries(blogContent)
  .slice(0, 5)
  .map(([slug, post]) => ({
    id: slug,
    title: post.title,
    excerpt: post.description,
    slug,
    date: post.date,
    readTime: post.readTime,
    category: post.category,
  }));

const categories = [
  'All Topics',
  'ERP Implementation',
  'Core Problems',
  'Institution-Specific',
  'Cloud',
  'Website-SEO',
];

const modules = [
  {
    title: 'Student Management',
    description:
      'Maintain student profiles, admissions information, academic records and other important student data in one centralized system.',
  },
  {
    title: 'Admissions',
    description:
      'Organize admission workflows and student onboarding while keeping applicant and enrollment information structured.',
  },
  {
    title: 'Attendance',
    description:
      'Manage attendance records and provide administrators and faculty with a centralized view of attendance information.',
  },
  {
    title: 'Fees & Payments',
    description:
      'Organize fee structures, payment information, receipts and outstanding balances for easier financial administration.',
  },
  {
    title: 'Examinations',
    description:
      'Support examination management, marks, results and academic reporting through a centralized platform.',
  },
  {
    title: 'Timetable Management',
    description:
      'Plan academic schedules and manage classes, subjects, faculty and timetable requirements more efficiently.',
  },
  {
    title: 'Faculty Management',
    description:
      'Keep faculty information and academic responsibilities organized alongside other institutional operations.',
  },
  {
    title: 'Academic Management',
    description:
      'Organize programs, subjects, semesters and academic structures within a connected institutional workflow.',
  },
  {
    title: 'Communication',
    description:
      'Help institutions organize important communication and information sharing between administrators, faculty and students.',
  },
  {
    title: 'Reports & Analytics',
    description:
      'Turn operational data into useful reports that help administrators understand institutional performance.',
  },
  {
    title: 'Administration',
    description:
      'Centralize everyday administrative workflows so teams can spend less time maintaining disconnected records.',
  },
];

const HomePage = () => {
  const navigate = useNavigate();

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All Topics');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleDemo = () => {
    navigate('/request-demo');
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    setMobileMenuOpen(false);
  };

  const filteredPosts =
    activeCategory === 'All Topics'
      ? featuredPosts
      : featuredPosts.filter((post) => post.category === activeCategory);

  return (
    <div className="homepage">
      {/* =========================================================
          NAVIGATION
      ========================================================== */}
      <header>
        <nav
          className={`nav ${isScrolled ? 'nav-scrolled' : ''}`}
          aria-label="Main navigation"
        >
          <div className="nav-container">
            <Link to="/" className="logo" aria-label="Truyon home">
              <img
                src={logo}
                alt="Truyon educational ERP"
                className="logo-icon-img"
              />
              <span className="logo-text">truyon</span>
            </Link>

            <div className="nav-center">
              <Link to="/blog" className="nav-link">
                Blog
              </Link>

              <button
                type="button"
                onClick={() => scrollToSection('solutions')}
              >
                Solutions
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('modules')}
              >
                Features
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('resources')}
              >
                Resources
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('about')}
              >
                About
              </button>
            </div>

            <div className="nav-right">
              <button
                type="button"
                onClick={handleSignIn}
                className="nav-signin"
              >
                Sign In
              </button>

              <button
                type="button"
                onClick={handleDemo}
                className="nav-demo"
              >
                Get Demo
              </button>

              <button
                type="button"
                onClick={handleSignUp}
                className="nav-start"
              >
                Start Free
              </button>
            </div>

            <button
              type="button"
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen((value) => !value)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="mobile-menu">
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
                Blog
              </Link>

              <button
                type="button"
                onClick={() => scrollToSection('solutions')}
              >
                Solutions
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('modules')}
              >
                Features
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('resources')}
              >
                Resources
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('about')}
              >
                About
              </button>

              <button type="button" onClick={handleSignIn}>
                Sign In
              </button>

              <button type="button" onClick={handleDemo}>
                Get Demo
              </button>

              <button
                type="button"
                onClick={handleSignUp}
                className="mobile-start"
              >
                Start Free
              </button>
            </div>
          )}
        </nav>
      </header>

      <main>
        {/* =========================================================
            HERO
        ========================================================== */}
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-container">
            <div className="hero-badge">
              <span className="badge-dot" />
              Educational Management Software
            </div>

            <h1 id="hero-title" className="hero-title">
              Modern ERP Software for
              <span className="hero-title-highlight">
                Educational Institutions
              </span>
            </h1>

            <p className="hero-subtitle">
              Truyon is a cloud-based education management platform designed
              to help schools, colleges and educational institutions organize
              admissions, students, academics, attendance, fees, examinations,
              timetables, communication and everyday administration in one
              connected system.
            </p>

            <div className="hero-buttons">
              <button
                type="button"
                onClick={handleDemo}
                className="hero-btn-primary"
              >
                Request a Demo →
              </button>

              <Link to="/blog" className="hero-btn-secondary">
                Explore Resources
              </Link>
            </div>

            <p className="hero-note">
              Built by Truyon Technologies (OPC) Private Limited in India.
            </p>
          </div>
        </section>

        {/* =========================================================
            PRODUCT OVERVIEW
        ========================================================== */}
        <section className="overview-section">
          <div className="overview-container">
            <div className="overview-copy">
              <span className="section-badge">One Connected Platform</span>

              <h2>
                Bring everyday educational administration into one system
              </h2>

              <p>
                Educational institutions manage large amounts of information
                every day. Admissions, student records, attendance, academic
                structures, fees, examinations, timetables and communication
                often involve different teams and processes.
              </p>

              <p>
                Truyon brings these workflows together through a centralized
                ERP platform. Administrators can organize institutional data,
                faculty can manage academic activities, and teams can work
                with information from a common system.
              </p>

              <div className="overview-points">
                <div>
                  <span>01</span>
                  <strong>Centralized information</strong>
                  <p>
                    Keep important institutional information organized in one
                    connected platform.
                  </p>
                </div>

                <div>
                  <span>02</span>
                  <strong>Connected workflows</strong>
                  <p>
                    Reduce unnecessary duplication between academic and
                    administrative processes.
                  </p>
                </div>

                <div>
                  <span>03</span>
                  <strong>Accessible operations</strong>
                  <p>
                    Give authorized users access to the workflows and
                    information they need.
                  </p>
                </div>
              </div>
            </div>

            <div className="product-preview" aria-label="Truyon ERP overview">
              <div className="preview-top">
                <div className="preview-dots">
                  <span />
                  <span />
                  <span />
                </div>
                <span>Truyon ERP</span>
              </div>

              <div className="preview-body">
                <div className="preview-sidebar">
                  <div className="preview-logo">T</div>
                  <div className="preview-line active" />
                  <div className="preview-line" />
                  <div className="preview-line" />
                  <div className="preview-line" />
                  <div className="preview-line" />
                </div>

                <div className="preview-content">
                  <div className="preview-heading" />
                  <div className="preview-cards">
                    <div />
                    <div />
                    <div />
                  </div>

                  <div className="preview-table">
                    <div />
                    <div />
                    <div />
                    <div />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            MODULES
        ========================================================== */}
        <section
          id="modules"
          className="modules-section"
          aria-labelledby="modules-title"
        >
          <div className="section-container">
            <div className="section-heading">
              <span className="section-badge">Platform Capabilities</span>

              <h2 id="modules-title">
                Tools for the complete institutional workflow
              </h2>

              <p>
                Truyon brings key academic and administrative activities
                together so institutions can manage daily operations through
                a structured digital platform.
              </p>
            </div>

            <div className="modules-grid">
              {modules.map((module, index) => (
                <article className="module-card" key={module.title}>
                  <span className="module-number">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  <h3>{module.title}</h3>

                  <p>{module.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* =========================================================
            SOLUTIONS
        ========================================================== */}
        <section
          id="solutions"
          className="solutions"
          aria-labelledby="solutions-title"
        >
          <div className="solutions-container">
            <div className="solutions-header">
              <span className="section-badge">Designed for Institutions</span>

              <h2 id="solutions-title" className="section-title">
                Built around real administrative workflows
              </h2>

              <p className="section-subtitle">
                A useful education ERP should make everyday work easier rather
                than add another layer of complexity. Truyon focuses on
                practical workflows, centralized information and accessible
                administration.
              </p>
            </div>

            <div className="solutions-grid">
              <article className="solution-card">
                <div className="solution-icon">🎓</div>
                <h3>Schools</h3>
                <p>
                  Manage students, admissions, attendance, fees, academics,
                  examinations and everyday school administration from a
                  connected platform.
                </p>
                <Link to="/request-demo" className="solution-learn">
                  Request a Demo →
                </Link>
              </article>

              <article className="solution-card">
                <div className="solution-icon">🏫</div>
                <h3>Colleges</h3>
                <p>
                  Organize programs, subjects, semesters, students, faculty,
                  examinations, timetables and other academic workflows.
                </p>
                <Link to="/request-demo" className="solution-learn">
                  Request a Demo →
                </Link>
              </article>

              <article className="solution-card">
                <div className="solution-icon">📚</div>
                <h3>Academic Teams</h3>
                <p>
                  Give faculty and administrators structured workflows for
                  managing academic information and daily institutional tasks.
                </p>
                <Link to="/request-demo" className="solution-learn">
                  Request a Demo →
                </Link>
              </article>

              <article className="solution-card">
                <div className="solution-icon">📊</div>
                <h3>Institutional Administration</h3>
                <p>
                  Bring operational information together and create reports
                  that help administrators understand ongoing activities.
                </p>
                <Link to="/request-demo" className="solution-learn">
                  Request a Demo →
                </Link>
              </article>
            </div>
          </div>
        </section>

        {/* =========================================================
            HOW IT WORKS
        ========================================================== */}
        <section className="how-section" aria-labelledby="how-title">
          <div className="section-container">
            <div className="section-heading">
              <span className="section-badge">How It Works</span>

              <h2 id="how-title">A structured path to digital administration</h2>

              <p>
                Truyon is designed around the way educational institutions
                organize people, programs, academic information and daily
                operations.
              </p>
            </div>

            <div className="steps-grid">
              <article className="step-card">
                <span>01</span>
                <h3>Set up your institution</h3>
                <p>
                  Configure the academic and administrative structure that
                  matches your institution.
                </p>
              </article>

              <article className="step-card">
                <span>02</span>
                <h3>Organize your data</h3>
                <p>
                  Bring student, faculty, academic and operational information
                  into structured workflows.
                </p>
              </article>

              <article className="step-card">
                <span>03</span>
                <h3>Run daily operations</h3>
                <p>
                  Use connected workflows for admissions, attendance, fees,
                  academics, examinations and other activities.
                </p>
              </article>

              <article className="step-card">
                <span>04</span>
                <h3>Review and improve</h3>
                <p>
                  Use reports and centralized information to understand
                  operations and support better decisions.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* =========================================================
            RESOURCES
        ========================================================== */}
        <section
          id="resources"
          className="featured-blog"
          aria-labelledby="resources-title"
        >
          <div className="blog-container">
            <div className="blog-header">
              <span className="section-badge">Truyon Resources</span>

              <h2 id="resources-title" className="section-title">
                Guides for educational technology
              </h2>

              <p className="section-subtitle">
                Practical articles covering ERP implementation, educational
                administration, digital transformation, infrastructure and
                technology decisions.
              </p>
            </div>

            <div className="category-tabs" role="tablist">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={`tab-btn ${
                    activeCategory === category ? 'active' : ''
                  }`}
                  onClick={() => setActiveCategory(category)}
                  role="tab"
                  aria-selected={activeCategory === category}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="blog-grid">
              {filteredPosts.map((post) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.id}
                  className="blog-card"
                >
                  <div className="blog-card-badge">{post.category}</div>

                  <h3 className="blog-card-title">{post.title}</h3>

                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-footer">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="blog-cta">
              <Link to="/blog" className="view-all-btn">
                View All Articles →
              </Link>
            </div>
          </div>
        </section>

        {/* =========================================================
            EDUCATIONAL CONTENT
        ========================================================== */}
        <section className="educational-content">
          <div className="content-container">
            <div className="content-left">
              <span className="section-badge">
                Understanding Education Technology
              </span>

              <h2>
                Choosing education management software is more than choosing a
                feature list
              </h2>

              <p>
                Educational institutions have different academic structures,
                administrative processes, staffing models and technology
                requirements. A platform that works well for one institution
                may require different configuration for another.
              </p>

              <p>
                When evaluating an ERP, institutions should look beyond the
                number of features and consider usability, workflow fit, data
                management, reporting, support, security and the ability of
                staff to use the system consistently.
              </p>

              <h3>What institutions should evaluate</h3>

              <ul className="findings-list">
                <li>
                  <strong>Workflow fit:</strong> Can the system support the
                  institution's actual academic and administrative processes?
                </li>

                <li>
                  <strong>Ease of use:</strong> Can administrators, faculty and
                  other users understand the workflows without unnecessary
                  complexity?
                </li>

                <li>
                  <strong>Data organization:</strong> Can important student,
                  academic and operational information be maintained
                  consistently?
                </li>

                <li>
                  <strong>Reporting:</strong> Can administrators access useful
                  information when they need it?
                </li>

                <li>
                  <strong>Scalability:</strong> Can the platform adapt as the
                  institution adds students, programs, classes and workflows?
                </li>
              </ul>

              <p>
                Truyon focuses on bringing these areas together through a
                centralized education management platform while keeping the
                experience practical for the people who use it every day.
              </p>
            </div>

            <aside className="content-right">
              <div className="stat-card">
                <div className="stat-number">11+</div>
                <div className="stat-label">Core ERP capabilities</div>
              </div>

              <div className="stat-card">
                <div className="stat-number">50+</div>
                <div className="stat-label">Educational technology articles</div>
              </div>

              <div className="stat-card">
                <div className="stat-number">1</div>
                <div className="stat-label">
                  Connected platform for institutional workflows
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* =========================================================
            WHY EDUCATORS CHOOSE TRUYON
        ========================================================== */}
        <section className="value-section" aria-labelledby="value-title">
          <div className="section-container">
            <div className="section-heading">
              <span className="section-badge">Why Institutions Choose Truyon</span>

              <h2 id="value-title">
                Practical ERP planning for schools and colleges that need order,
                clarity and better coordination
              </h2>

              <p>
                Many educational institutions do not struggle because they lack
                data. They struggle because student information, academic data,
                attendance records, fees and communication are spread across
                disconnected systems or manual processes. This creates delays,
                duplication and confusion across teams.
              </p>
            </div>

            <div className="value-grid">
              <article className="value-card">
                <div className="value-icon">📌</div>
                <h3>Less administrative chaos</h3>
                <p>
                  Truyon helps schools and colleges bring core operations into a
                  structured digital workflow so key information is easier to
                  track and maintain.
                </p>
              </article>

              <article className="value-card">
                <div className="value-icon">🧑‍🏫</div>
                <h3>Better coordination across teams</h3>
                <p>
                  Administrators, faculty and support teams can work with the same
                  information instead of depending on fragmented records and
                  repeated manual updates.
                </p>
              </article>

              <article className="value-card">
                <div className="value-icon">📈</div>
                <h3>More visibility into operations</h3>
                <p>
                  When institutional data is organized well, it becomes easier to
                  understand performance, identify bottlenecks and improve
                  recurring processes.
                </p>
              </article>

              <article className="value-card">
                <div className="value-icon">🔒</div>
                <h3>Operational continuity</h3>
                <p>
                  Education management software should create stability, not add
                  another layer of complexity. Truyon is built to support practical
                  institutional workflows and better day-to-day coordination.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* =========================================================
            ABOUT
        ========================================================== */}
        <section
          id="about"
          className="about-section"
          aria-labelledby="about-title"
        >
          <div className="about-container">
            <div className="about-text">
              <span className="section-badge">About Truyon</span>

              <h2 id="about-title">
                Building practical technology for educational institutions
              </h2>

              <p>
                Truyon is an education technology company developing software
                for educational institutions. Our focus is on creating
                practical digital workflows that help institutions organize
                academic and administrative operations.
              </p>

              <p>
                Truyon Technologies (OPC) Private Limited is registered in
                India. The company is based in Bangalore, Karnataka.
              </p>

              <div className="about-stats">
                <div>
                  <h4>11+</h4>
                  <p>ERP capabilities</p>
                </div>

                <div>
                  <h4>50+</h4>
                  <p>Resources</p>
                </div>

                <div>
                  <h4>India</h4>
                  <p>Company base</p>
                </div>

                <div>
                  <h4>2026</h4>
                  <p>Current platform generation</p>
                </div>
              </div>
            </div>

            <aside className="about-contact">
              <h3>Contact Truyon</h3>

              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:support@truyon.com">
                  support@truyon.com
                </a>
              </p>

              <p>
                <strong>Phone:</strong>{' '}
                <a href="tel:+918088829438">+91 8088829438</a>
              </p>

              <p>
                <strong>Location:</strong> Bangalore, Karnataka, India
              </p>

              <p>
                <strong>CIN:</strong> U62013KA2026OPC221006
              </p>

              <Link to="/contact-us" className="contact-btn">
                Contact Us →
              </Link>
            </aside>
          </div>
        </section>

        {/* =========================================================
            FAQ
        ========================================================== */}
        <section className="faq-section" aria-labelledby="faq-title">
          <div className="faq-container">
            <div className="section-heading">
              <span className="section-badge">Frequently Asked Questions</span>

              <h2 id="faq-title">About Truyon ERP</h2>
            </div>

            <div className="faq-list">
              <details>
                <summary>What is Truyon?</summary>
                <p>
                  Truyon is an education management platform designed to help
                  educational institutions organize academic and administrative
                  workflows in one connected system.
                </p>
              </details>

              <details>
                <summary>Who can use Truyon?</summary>
                <p>
                  Truyon is designed for educational institutions and teams
                  that need to manage student, academic and administrative
                  workflows digitally.
                </p>
              </details>

              <details>
                <summary>What can Truyon help institutions manage?</summary>
                <p>
                  Depending on the configured modules, Truyon can support areas
                  such as admissions, student management, attendance, fees,
                  academics, examinations, timetables, faculty management,
                  communication and reporting.
                </p>
              </details>

              <details>
                <summary>Can I request a demonstration?</summary>
                <p>
                  Yes. Use the Request a Demo option to contact the Truyon team
                  and discuss your institution's requirements.
                </p>
              </details>

              <details>
                <summary>Where is Truyon based?</summary>
                <p>
                  Truyon Technologies (OPC) Private Limited is based in
                  Bangalore, Karnataka, India.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* =========================================================
            FINAL CTA
        ========================================================== */}
        <section className="final-cta">
          <div className="final-cta-container">
            <span className="final-eyebrow">
              Education Management Platform
            </span>

            <h2>Ready to explore Truyon?</h2>

            <p>
              Learn how Truyon can help organize your institution's academic
              and administrative workflows through a connected digital
              platform.
            </p>

            <div className="final-buttons">
              <Link to="/blog" className="final-btn-secondary">
                Explore Resources →
              </Link>

              <button
                type="button"
                onClick={handleDemo}
                className="final-btn-primary"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* =========================================================
          FOOTER
      ========================================================== */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Link to="/" className="footer-logo">
                <img
                  src={logo}
                  alt="Truyon"
                  className="footer-logo-img"
                />
                <span>truyon</span>
              </Link>

              <p className="footer-tagline">
                Education technology for institutions that want to organize
                academic and administrative operations through a connected
                digital platform.
              </p>
            </div>

            <div className="footer-links">
              <h4>Platform</h4>

              <button
                type="button"
                onClick={() => scrollToSection('modules')}
              >
                Features
              </button>

              <button
                type="button"
                onClick={() => scrollToSection('solutions')}
              >
                Solutions
              </button>

              <Link to="/request-demo">Request Demo</Link>
              <Link to="/signup">Start Free</Link>
            </div>

            <div className="footer-links">
              <h4>Resources</h4>

              <Link to="/blog">Blog</Link>
              <Link to="/blog">Articles</Link>

              <button
                type="button"
                onClick={() => scrollToSection('faq-title')}
              >
                FAQ
              </button>

              <Link to="/contact-us">Contact</Link>
            </div>

            <div className="footer-links">
              <h4>Company</h4>

              <Link to="/about-us">About Us</Link>
              <Link to="/contact-us">Contact</Link>
              <Link to="/terms-of-service">Terms of Service</Link>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/refund-policy">Refund Policy</Link>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              © {new Date().getFullYear()} Truyon Technologies (OPC) Private
              Limited. All rights reserved.
            </p>

            <p>
              Bangalore, Karnataka, India
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        * {
          box-sizing: border-box;
        }

        .homepage {
          font-family:
            -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto,
            Helvetica, Arial, sans-serif;
          background: #ffffff;
          color: #1d1d1f;
          line-height: 1.65;
          overflow-x: hidden;
        }

        button,
        input,
        textarea,
        select {
          font: inherit;
        }

        button {
          cursor: pointer;
        }

        a {
          color: inherit;
        }

        /* =========================
           NAVIGATION
        ========================== */

        .nav {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1000;
          padding: 1rem 0;
          transition:
            background 0.25s ease,
            box-shadow 0.25s ease,
            padding 0.25s ease;
        }

        .nav-scrolled {
          padding: 0.55rem 0;
          background: rgba(255, 255, 255, 0.96);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
        }

        .nav-container {
          max-width: 1240px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 2rem;
        }

        .logo {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          text-decoration: none;
          font-size: 1.3rem;
          font-weight: 700;
          letter-spacing: -0.03em;
          white-space: nowrap;
        }

        .logo-icon-img {
          width: 34px;
          height: 34px;
          object-fit: contain;
        }

        .nav-center {
          display: flex;
          align-items: center;
          gap: 1.7rem;
          margin-left: auto;
        }

        .nav-center button,
        .nav-link {
          background: transparent;
          border: 0;
          color: #3a3a3c;
          font-size: 0.9rem;
          text-decoration: none;
          padding: 0.45rem 0;
          transition: color 0.2s ease;
        }

        .nav-center button:hover,
        .nav-link:hover {
          color: #0066cc;
        }

        .nav-right {
          display: flex;
          align-items: center;
          gap: 0.65rem;
        }

        .nav-signin,
        .nav-demo,
        .nav-start {
          border-radius: 999px;
          font-size: 0.88rem;
          padding: 0.62rem 1.15rem;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease,
            transform 0.2s ease;
        }

        .nav-signin {
          background: transparent;
          border: 1px solid transparent;
          color: #222;
        }

        .nav-signin:hover {
          color: #0066cc;
        }

        .nav-demo {
          background: #fff;
          border: 1px solid #0071e3;
          color: #0071e3;
        }

        .nav-demo:hover {
          background: #0071e3;
          color: #fff;
        }

        .nav-start {
          background: #0071e3;
          border: 1px solid #0071e3;
          color: #fff;
        }

        .nav-start:hover {
          background: #005fc1;
          transform: translateY(-1px);
        }

        .mobile-menu-btn {
          display: none;
          border: 0;
          background: transparent;
          color: #111;
          font-size: 1.5rem;
          padding: 0.35rem;
        }

        .mobile-menu {
          display: none;
        }

        /* =========================
           COMMON
        ========================== */

        .section-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-heading {
          max-width: 760px;
          margin: 0 auto 3.5rem;
          text-align: center;
        }

        .section-badge {
          display: inline-block;
          color: #0066cc;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .section-heading h2,
        .overview-copy h2,
        .content-left h2,
        .about-text h2 {
          color: #161618;
          font-size: clamp(2rem, 4vw, 3rem);
          line-height: 1.12;
          letter-spacing: -0.04em;
          margin: 0.7rem 0 1.15rem;
        }

        .section-heading p {
          color: #66666b;
          font-size: 1.08rem;
          margin: 0;
        }

        /* =========================
           HERO
        ========================== */

        .hero {
          padding: 10.5rem 2rem 6rem;
          background:
            radial-gradient(
              circle at 50% 0%,
              rgba(0, 113, 227, 0.08),
              transparent 38%
            ),
            #fff;
        }

        .hero-container {
          max-width: 980px;
          margin: 0 auto;
          text-align: center;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.48rem 0.9rem;
          background: #f5f7fa;
          border: 1px solid #e8ebef;
          border-radius: 999px;
          color: #45464a;
          font-size: 0.82rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .badge-dot {
          width: 7px;
          height: 7px;
          background: #24a148;
          border-radius: 50%;
        }

        .hero-title {
          max-width: 920px;
          margin: 0 auto 1.4rem;
          color: #171719;
          font-size: clamp(2.7rem, 6vw, 5rem);
          line-height: 1.02;
          letter-spacing: -0.055em;
          font-weight: 750;
        }

        .hero-title-highlight {
          display: block;
          color: #0071e3;
          margin-top: 0.2rem;
        }

        .hero-subtitle {
          max-width: 780px;
          margin: 0 auto 2rem;
          color: #66666b;
          font-size: clamp(1rem, 2vw, 1.2rem);
          line-height: 1.75;
        }

        .hero-buttons {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.8rem;
          flex-wrap: wrap;
          margin-bottom: 1.1rem;
        }

        .hero-btn-primary,
        .hero-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          font-size: 0.96rem;
          font-weight: 600;
          text-decoration: none;
        }

        .hero-btn-primary {
          border: 1px solid #0071e3;
          background: #0071e3;
          color: #fff;
        }

        .hero-btn-primary:hover {
          background: #005fc1;
        }

        .hero-btn-secondary {
          border: 1px solid #d5d7da;
          background: #fff;
          color: #222;
        }

        .hero-btn-secondary:hover {
          border-color: #0071e3;
          color: #0071e3;
        }

        .hero-note {
          color: #88898d;
          font-size: 0.82rem;
          margin: 0;
        }

        /* =========================
           OVERVIEW
        ========================== */

        .overview-section {
          padding: 6rem 2rem;
          background: #f8f9fb;
          border-top: 1px solid #eef0f3;
          border-bottom: 1px solid #eef0f3;
        }

        .overview-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          align-items: center;
          gap: 5rem;
        }

        .overview-copy h2 {
          max-width: 650px;
        }

        .overview-copy > p {
          color: #56575c;
          max-width: 700px;
          margin: 0 0 1rem;
        }

        .overview-points {
          margin-top: 2rem;
          display: grid;
          gap: 1rem;
        }

        .overview-points > div {
          display: grid;
          grid-template-columns: 42px 1fr;
          column-gap: 0.9rem;
          padding: 1rem 0;
          border-top: 1px solid #e3e6ea;
        }

        .overview-points span {
          grid-row: span 2;
          color: #0071e3;
          font-size: 0.8rem;
          font-weight: 800;
        }

        .overview-points strong {
          color: #202124;
          font-size: 0.98rem;
        }

        .overview-points p {
          margin: 0.25rem 0 0;
          color: #6c6d72;
          font-size: 0.9rem;
        }

        .product-preview {
          min-height: 430px;
          overflow: hidden;
          background: #fff;
          border: 1px solid #e2e5e9;
          border-radius: 24px;
          box-shadow: 0 25px 70px rgba(20, 30, 45, 0.09);
        }

        .preview-top {
          height: 52px;
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0 1rem;
          border-bottom: 1px solid #edf0f3;
          color: #55575b;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .preview-dots {
          display: flex;
          gap: 5px;
        }

        .preview-dots span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #d7d9dc;
        }

        .preview-body {
          min-height: 378px;
          display: grid;
          grid-template-columns: 72px 1fr;
        }

        .preview-sidebar {
          padding: 1rem 0.75rem;
          background: #f7f8fa;
          border-right: 1px solid #edf0f3;
        }

        .preview-logo {
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          margin: 0 auto 1.4rem;
          border-radius: 9px;
          background: #0071e3;
          color: #fff;
          font-weight: 800;
        }

        .preview-line {
          height: 9px;
          margin: 0.9rem 0;
          border-radius: 999px;
          background: #dfe2e6;
        }

        .preview-line.active {
          background: #9fcdf8;
        }

        .preview-content {
          padding: 1.5rem;
        }

        .preview-heading {
          width: 42%;
          height: 17px;
          border-radius: 5px;
          background: #d8dce1;
          margin-bottom: 1.5rem;
        }

        .preview-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.8rem;
          margin-bottom: 1.2rem;
        }

        .preview-cards div {
          height: 88px;
          border: 1px solid #e7e9ec;
          border-radius: 12px;
          background: #fafbfc;
        }

        .preview-table {
          border: 1px solid #e7e9ec;
          border-radius: 12px;
          overflow: hidden;
        }

        .preview-table div {
          height: 38px;
          border-bottom: 1px solid #edf0f2;
          background: #fff;
        }

        .preview-table div:nth-child(even) {
          background: #fafbfc;
        }

        /* =========================
           MODULES
        ========================== */

        .modules-section {
          padding: 7rem 2rem;
          background: #fff;
        }

        .modules-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .module-card {
          position: relative;
          min-height: 230px;
          padding: 1.8rem;
          border: 1px solid #e7e9ec;
          border-radius: 20px;
          background: #fff;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease;
        }

        .module-card:hover {
          transform: translateY(-4px);
          border-color: #d5e7f9;
          box-shadow: 0 16px 40px rgba(20, 30, 45, 0.07);
        }

        .module-number {
          display: inline-block;
          margin-bottom: 2rem;
          color: #0071e3;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 0.08em;
        }

        .module-card h3 {
          margin: 0 0 0.6rem;
          color: #1d1d1f;
          font-size: 1.15rem;
        }

        .module-card p {
          margin: 0;
          color: #68696d;
          font-size: 0.92rem;
          line-height: 1.65;
        }

        /* =========================
           SOLUTIONS
        ========================== */

        .solutions {
          padding: 7rem 2rem;
          background: #f8f9fb;
          border-top: 1px solid #edf0f2;
          border-bottom: 1px solid #edf0f2;
        }

        .solutions-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .solutions-header {
          max-width: 780px;
          margin: 0 auto 3rem;
          text-align: center;
        }

        .section-title {
          margin: 0.7rem 0 1rem;
          color: #171719;
          font-size: clamp(2rem, 4vw, 3rem);
          line-height: 1.12;
          letter-spacing: -0.04em;
        }

        .section-subtitle {
          max-width: 720px;
          margin: 0 auto;
          color: #696a6f;
          font-size: 1.05rem;
        }

        .solutions-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .solution-card {
          padding: 2rem;
          background: #fff;
          border: 1px solid #e7e9ec;
          border-radius: 20px;
        }

        .solution-icon {
          width: 46px;
          height: 46px;
          display: grid;
          place-items: center;
          margin-bottom: 1rem;
          border-radius: 13px;
          background: #eef6ff;
          font-size: 1.35rem;
        }

        .solution-card h3 {
          margin: 0 0 0.55rem;
          font-size: 1.15rem;
        }

        .solution-card p {
          margin: 0;
          color: #696a6f;
          font-size: 0.92rem;
        }

        .solution-learn {
          display: inline-block;
          margin-top: 1rem;
          color: #0071e3;
          font-size: 0.88rem;
          font-weight: 600;
          text-decoration: none;
        }

        .solution-learn:hover {
          text-decoration: underline;
        }

        /* =========================
           HOW IT WORKS
        ========================== */

        .how-section {
          padding: 7rem 2rem;
          background: #fff;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }

        .step-card {
          padding: 1.8rem;
          border-top: 2px solid #0071e3;
          background: #f8f9fb;
        }

        .step-card > span {
          color: #0071e3;
          font-size: 0.78rem;
          font-weight: 800;
        }

        .step-card h3 {
          margin: 1.2rem 0 0.65rem;
          font-size: 1.08rem;
        }

        .step-card p {
          margin: 0;
          color: #696a6f;
          font-size: 0.9rem;
        }

        /* =========================
           VALUE SECTION
        ========================== */

        .value-section {
          padding: 5rem 2rem 2rem;
          background: #f8fafc;
        }

        .value-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 1.5rem;
          margin-top: 2.5rem;
        }

        .value-card {
          background: #fff;
          border: 1px solid rgba(148, 163, 184, 0.18);
          border-radius: 24px;
          padding: 1.6rem 1.4rem;
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
        }

        .value-icon {
          width: 52px;
          height: 52px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          background: linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%);
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .value-card h3 {
          margin: 0 0 0.75rem;
          font-size: 1.15rem;
          line-height: 1.35;
          letter-spacing: -0.02em;
        }

        .value-card p {
          margin: 0;
          color: #475569;
          font-size: 0.96rem;
        }

        /* =========================
           BLOG
        ========================== */

        .featured-blog {
          padding: 7rem 2rem;
          background: #f8f9fb;
        }

        .blog-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .blog-header {
          max-width: 760px;
          margin: 0 auto 2.5rem;
          text-align: center;
        }

        .blog-header .section-title {
          margin-bottom: 1rem;
        }

        .category-tabs {
          display: flex;
          justify-content: center;
          gap: 0.55rem;
          flex-wrap: wrap;
          margin-bottom: 2rem;
        }

        .tab-btn {
          padding: 0.55rem 1rem;
          border: 1px solid #dfe2e6;
          border-radius: 999px;
          background: #fff;
          color: #55565a;
          font-size: 0.82rem;
        }

        .tab-btn:hover {
          border-color: #0071e3;
        }

        .tab-btn.active {
          background: #0071e3;
          border-color: #0071e3;
          color: #fff;
        }

        .blog-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }

        .blog-card {
          display: flex;
          min-height: 310px;
          flex-direction: column;
          padding: 1.6rem;
          border: 1px solid #e6e8eb;
          border-radius: 20px;
          background: #fff;
          text-decoration: none;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .blog-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(20, 30, 45, 0.08);
        }

        .blog-card-badge {
          align-self: flex-start;
          margin-bottom: 1rem;
          padding: 0.28rem 0.65rem;
          border-radius: 999px;
          background: #eef6ff;
          color: #0066cc;
          font-size: 0.68rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .blog-card-title {
          margin: 0 0 0.7rem;
          color: #202124;
          font-size: 1.05rem;
          line-height: 1.4;
        }

        .blog-card-excerpt {
          flex: 1;
          margin: 0;
          color: #696a6f;
          font-size: 0.9rem;
          line-height: 1.65;
        }

        .blog-card-footer {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 1.4rem;
          padding-top: 1rem;
          border-top: 1px solid #eceef0;
          color: #8a8b8f;
          font-size: 0.75rem;
        }

        .blog-cta {
          margin-top: 2rem;
          text-align: center;
        }

        .view-all-btn {
          display: inline-flex;
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          background: #0071e3;
          color: #fff;
          text-decoration: none;
          font-size: 0.92rem;
          font-weight: 600;
        }

        .view-all-btn:hover {
          background: #005fc1;
        }

        /* =========================
           EDUCATIONAL CONTENT
        ========================== */

        .educational-content {
          padding: 7rem 2rem;
          background: #fff;
        }

        .content-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.7fr 0.8fr;
          gap: 5rem;
          align-items: start;
        }

        .content-left h2 {
          max-width: 850px;
        }

        .content-left > p {
          max-width: 850px;
          color: #55565b;
          margin: 0 0 1.1rem;
        }

        .content-left h3 {
          margin: 2rem 0 0.5rem;
          font-size: 1.2rem;
        }

        .findings-list {
          margin: 1.2rem 0;
          padding: 0;
          list-style: none;
        }

        .findings-list li {
          padding: 0.95rem 0;
          border-bottom: 1px solid #eceef0;
          color: #5c5d61;
        }

        .findings-list strong {
          color: #202124;
        }

        .content-right {
          display: grid;
          gap: 1rem;
        }

        .stat-card {
          padding: 1.8rem;
          border: 1px solid #e4e7eb;
          border-radius: 18px;
          background: #f8f9fb;
          text-align: center;
        }

        .stat-number {
          color: #0071e3;
          font-size: 2.35rem;
          font-weight: 750;
          line-height: 1.1;
        }

        .stat-label {
          margin-top: 0.45rem;
          color: #696a6f;
          font-size: 0.86rem;
        }

        /* =========================
           ABOUT
        ========================== */

        .about-section {
          padding: 7rem 2rem;
          background: #f8f9fb;
          border-top: 1px solid #edf0f2;
        }

        .about-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.5fr 0.8fr;
          gap: 5rem;
        }

        .about-text > p {
          max-width: 800px;
          color: #57585d;
        }

        .about-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-top: 2rem;
        }

        .about-stats > div {
          padding-top: 1rem;
          border-top: 1px solid #dfe2e6;
        }

        .about-stats h4 {
          margin: 0;
          color: #0071e3;
          font-size: 1.6rem;
        }

        .about-stats p {
          margin: 0.2rem 0 0;
          color: #737478;
          font-size: 0.78rem;
        }

        .about-contact {
          align-self: start;
          padding: 2rem;
          border-radius: 20px;
          background: #fff;
          border: 1px solid #e5e8eb;
        }

        .about-contact h3 {
          margin: 0 0 1.2rem;
          font-size: 1.2rem;
        }

        .about-contact p {
          margin: 0.75rem 0;
          color: #55565a;
          font-size: 0.88rem;
        }

        .about-contact a {
          color: #0066cc;
          text-decoration: none;
        }

        .contact-btn {
          display: inline-flex;
          margin-top: 1rem;
          padding: 0.7rem 1.25rem;
          border-radius: 999px;
          background: #0071e3;
          color: #fff !important;
          font-weight: 600;
        }

        /* =========================
           FAQ
        ========================== */

        .faq-section {
          padding: 7rem 2rem;
          background: #fff;
        }

        .faq-container {
          max-width: 900px;
          margin: 0 auto;
        }

        .faq-container .section-heading {
          margin-bottom: 2rem;
        }

        .faq-list {
          border-top: 1px solid #e5e7ea;
        }

        .faq-list details {
          border-bottom: 1px solid #e5e7ea;
        }

        .faq-list summary {
          padding: 1.2rem 0;
          color: #202124;
          font-weight: 650;
          cursor: pointer;
          list-style: none;
        }

        .faq-list summary::-webkit-details-marker {
          display: none;
        }

        .faq-list summary::after {
          content: "+";
          float: right;
          color: #0071e3;
          font-size: 1.2rem;
          font-weight: 400;
        }

        .faq-list details[open] summary::after {
          content: "−";
        }

        .faq-list details p {
          max-width: 800px;
          margin: 0 0 1.2rem;
          color: #66676b;
          font-size: 0.92rem;
        }

        /* =========================
           CTA
        ========================== */

        .final-cta {
          padding: 7rem 2rem;
          background: #171719;
          color: #fff;
          text-align: center;
        }

        .final-cta-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .final-eyebrow {
          color: #72b7ff;
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .final-cta h2 {
          margin: 0.7rem 0 1rem;
          font-size: clamp(2rem, 4vw, 3.2rem);
          line-height: 1.1;
          letter-spacing: -0.04em;
        }

        .final-cta p {
          max-width: 680px;
          margin: 0 auto 2rem;
          color: #b6b7bb;
          font-size: 1.05rem;
        }

        .final-buttons {
          display: flex;
          justify-content: center;
          gap: 0.8rem;
          flex-wrap: wrap;
        }

        .final-btn-primary,
        .final-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          font-size: 0.92rem;
          font-weight: 600;
          text-decoration: none;
        }

        .final-btn-primary {
          border: 1px solid #0071e3;
          background: #0071e3;
          color: #fff;
        }

        .final-btn-primary:hover {
          background: #1682f2;
        }

        .final-btn-secondary {
          border: 1px solid #55565a;
          color: #fff;
        }

        .final-btn-secondary:hover {
          border-color: #fff;
        }

        /* =========================
           FOOTER
        ========================== */

        .footer {
          padding: 4rem 2rem 2rem;
          background: #f5f6f8;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1.8fr 1fr 1fr 1fr;
          gap: 3rem;
          padding-bottom: 3rem;
        }

        .footer-logo {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #202124;
          font-size: 1.25rem;
          font-weight: 700;
          text-decoration: none;
        }

        .footer-logo-img {
          width: 29px;
          height: 29px;
          object-fit: contain;
        }

        .footer-tagline {
          max-width: 390px;
          margin: 1rem 0 0;
          color: #696a6f;
          font-size: 0.86rem;
          line-height: 1.7;
        }

        .footer-links {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .footer-links h4 {
          margin: 0 0 0.8rem;
          color: #202124;
          font-size: 0.85rem;
        }

        .footer-links a,
        .footer-links button {
          padding: 0.32rem 0;
          border: 0;
          background: transparent;
          color: #696a6f;
          font-size: 0.83rem;
          text-decoration: none;
          text-align: left;
        }

        .footer-links a:hover,
        .footer-links button:hover {
          color: #0071e3;
        }

        .footer-bottom {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          padding-top: 1.5rem;
          border-top: 1px solid #dfe1e4;
          color: #7b7c80;
          font-size: 0.76rem;
        }

        .footer-bottom p {
          margin: 0;
        }

        /* =========================
           RESPONSIVE
        ========================== */

        @media (max-width: 1000px) {
          .nav-center {
            gap: 1rem;
          }

          .nav-right {
            gap: 0.3rem;
          }

          .overview-container,
          .content-container,
          .about-container {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .modules-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .value-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .steps-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .blog-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .footer-grid {
            grid-template-columns: 2fr 1fr 1fr;
          }
        }

        @media (max-width: 768px) {
          .nav-container {
            padding: 0 1.1rem;
          }

          .nav-center,
          .nav-right {
            display: none;
          }

          .mobile-menu-btn {
            display: block;
          }

          .mobile-menu {
            display: flex;
            flex-direction: column;
            gap: 0;
            padding: 0.7rem 1.1rem 1rem;
            background: #fff;
            border-top: 1px solid #edf0f2;
            box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
          }

          .mobile-menu a,
          .mobile-menu button {
            padding: 0.8rem 0;
            border: 0;
            background: transparent;
            color: #252629;
            text-align: left;
            text-decoration: none;
          }

          .mobile-start {
            padding: 0.75rem 1rem !important;
            border-radius: 10px !important;
            background: #0071e3 !important;
            color: #fff !important;
            text-align: center !important;
            margin-top: 0.35rem;
          }

          .hero {
            padding: 8rem 1.2rem 4.5rem;
          }

          .hero-title {
            font-size: clamp(2.35rem, 12vw, 3.5rem);
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .hero-buttons {
            flex-direction: column;
          }

          .hero-btn-primary,
          .hero-btn-secondary {
            width: 100%;
            max-width: 340px;
          }

          .overview-section,
          .modules-section,
          .solutions,
          .how-section,
          .featured-blog,
          .educational-content,
          .about-section,
          .faq-section,
          .final-cta {
            padding: 5rem 1.2rem;
          }

          .product-preview {
            min-height: 320px;
          }

          .preview-body {
            min-height: 268px;
          }

          .modules-grid,
          .solutions-grid,
          .blog-grid,
          .value-grid {
            grid-template-columns: 1fr;
          }

          .steps-grid {
            grid-template-columns: 1fr;
          }

          .about-stats {
            grid-template-columns: 1fr 1fr;
          }

          .footer {
            padding: 3rem 1.2rem 1.5rem;
          }

          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
          }

          .footer-brand {
            grid-column: 1 / -1;
          }

          .footer-bottom {
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .logo {
            font-size: 1.15rem;
          }

          .logo-icon-img {
            width: 30px;
            height: 30px;
          }

          .about-stats {
            grid-template-columns: 1fr 1fr;
          }

          .preview-content {
            padding: 1rem;
          }

          .preview-cards {
            grid-template-columns: 1fr;
          }

          .preview-cards div {
            height: 55px;
          }

          .footer-grid {
            grid-template-columns: 1fr;
          }

          .footer-brand {
            grid-column: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;